import { useMemo, useState } from 'react'
import { CommandCenterShell } from '../../shell'
import { StatusBadge } from '../OperationsMiniPortal'
import { DAILY_MEETINGS_ACCENT } from '../operationsModules'
import '../operations-surface.css'
import {
  MEETING_TYPES,
  emptyMeetingDraft,
  summarizeMeeting,
  nextActionStatus,
  type DailyMeetingRecord,
  type DailyMeetingDraft,
  type MeetingType,
} from './dailyMeetings'

/**
 * DailyMeetingsWorkroom — the real, persisted Daily Meetings surface.
 *
 * PHASE_2D: presentational + SSR-safe. The route wrapper owns useDailyMeetings
 * (live fetch) and passes state + callbacks. This component renders inside the
 * shared CommandCenterShell and NEVER fabricates a meeting — empty / loading /
 * auth-required / error are all honest states. Cal.com scheduling is shown as
 * INTEGRATION_DEFERRED (future only) and is not wired.
 *
 * VISUAL-FIDELITY CONVERSION: adopts the shared `.sqv-surface` foundation and
 * the Quantum Command visual system (operations-surface.css). All presentation
 * moved out of inline style objects into CSS; the only inline style is the
 * DYNAMIC per-surface accent (`--sqv-accent`, violet). Behavior + copy unchanged.
 */
export interface DailyMeetingsWorkroomProps {
  meetings: DailyMeetingRecord[]
  loading: boolean
  error: string | null
  authRequired: boolean
  today: string
  onCreate: (draft: DailyMeetingDraft) => Promise<DailyMeetingRecord | void>
  onUpdate: (id: string, patch: Partial<DailyMeetingDraft>) => Promise<DailyMeetingRecord | void>
  onReload?: () => void
  onHome?: () => void
  onBackCommandCenter?: () => void
  onBackOperations?: () => void
  onOpenRoute?: (to: string) => void
}

const accentVar = { ['--sqv-accent']: DAILY_MEETINGS_ACCENT } as React.CSSProperties

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="sqv-ops-section">
      <h3 className="sqv-ops-section__title">{title}</h3>
      {children}
    </section>
  )
}

/** A simple "add + list" editor for a text-list field (decisions, blockers, readiness). */
function ListEditor({ label, items, placeholder, onAdd }: { label: string; items: string[]; placeholder: string; onAdd: (text: string) => void }) {
  const [draft, setDraft] = useState('')
  const add = () => { if (draft.trim()) { onAdd(draft.trim()); setDraft('') } }
  return (
    <div>
      {items.length === 0 ? (
        <p className="sqv-ops-dm-empty">No {label.toLowerCase()} yet.</p>
      ) : (
        <ul className="sqv-ops-dm-list">
          {items.map((t, i) => <li key={i} className="sqv-ops-dm-li">{t}</li>)}
        </ul>
      )}
      <div className="sqv-ops-dm-add">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add() }} placeholder={placeholder} aria-label={placeholder} className="sqv-ops-input" />
        <button type="button" onClick={add} className="sqv-ops-btn">Add</button>
      </div>
    </div>
  )
}

function MeetingEditor({ meeting, onUpdate }: { meeting: DailyMeetingRecord; onUpdate: DailyMeetingsWorkroomProps['onUpdate'] }) {
  const [summary, setSummary] = useState(meeting.last_24h_summary ?? '')
  const [plan, setPlan] = useState(meeting.next_24h_plan ?? '')
  const [followUp, setFollowUp] = useState(meeting.follow_up_needed)
  const [savingNotes, setSavingNotes] = useState(false)
  const [actionDraft, setActionDraft] = useState('')

  const saveNotes = async () => {
    setSavingNotes(true)
    try { await onUpdate(meeting.id, { last_24h_summary: summary, next_24h_plan: plan, follow_up_needed: followUp }) }
    finally { setSavingNotes(false) }
  }

  const addDecision = (text: string) => onUpdate(meeting.id, { decisions: [...meeting.decisions, { text }] })
  const addBlocker = (text: string) => onUpdate(meeting.id, { blockers: [...meeting.blockers, { text }] })
  const addReadiness = (text: string) => onUpdate(meeting.id, { readiness_changes: [...meeting.readiness_changes, { text }] })
  const addAction = () => { if (actionDraft.trim()) { onUpdate(meeting.id, { action_items: [...meeting.action_items, { text: actionDraft.trim(), status: 'open' }] }); setActionDraft('') } }
  const cycleAction = (idx: number) => onUpdate(meeting.id, { action_items: meeting.action_items.map((a, i) => i === idx ? { ...a, status: nextActionStatus(a.status) } : a) })

  return (
    <>
      <Section title={`Today's Meeting · ${meeting.meeting_title}`}>
        <div className="sqv-ops-dm-field">
          <label className="sqv-ops-dm-label">LAST 24H SUMMARY</label>
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What happened since the last meeting…" rows={3} className="sqv-ops-textarea" />
          <label className="sqv-ops-dm-label">NEXT 24H PLAN</label>
          <textarea value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="What we'll do before the next meeting…" rows={3} className="sqv-ops-textarea" />
          <div className="sqv-ops-dm-controls">
            <button type="button" onClick={saveNotes} disabled={savingNotes} className="sqv-ops-btn">{savingNotes ? 'Saving…' : 'Save notes'}</button>
            <label className="sqv-ops-dm-check">
              <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} /> Follow-up needed
            </label>
          </div>
        </div>
      </Section>

      <Section title="Decisions">
        <ListEditor label="decisions" items={meeting.decisions.map((d) => d.text)} placeholder="Record a decision…" onAdd={addDecision} />
      </Section>

      <Section title="Blockers">
        <ListEditor label="blockers" items={meeting.blockers.map((b) => b.text)} placeholder="Add a blocker…" onAdd={addBlocker} />
      </Section>

      <Section title="Action Items">
        {meeting.action_items.length === 0 ? (
          <p className="sqv-ops-dm-empty">No action items yet.</p>
        ) : (
          <div className="sqv-ops-dm-actions">
            {meeting.action_items.map((a, i) => (
              <div key={i} className="sqv-ops-dm-action">
                <button type="button" onClick={() => cycleAction(i)} title="Cycle status: open → done → blocked" className="sqv-ops-dm-action__status" data-status={a.status}>{a.status.toUpperCase()}</button>
                <span className="sqv-ops-dm-action__text" data-done={a.status === 'done'}>{a.text}</span>
                {a.owner && <span className="sqv-ops-dm-action__owner">@{a.owner}</span>}
              </div>
            ))}
          </div>
        )}
        <div className="sqv-ops-dm-add">
          <input value={actionDraft} onChange={(e) => setActionDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addAction() }} placeholder="Add an action item…" aria-label="Add an action item" className="sqv-ops-input" />
          <button type="button" onClick={addAction} className="sqv-ops-btn">Add</button>
        </div>
      </Section>

      <Section title="Readiness Changes">
        <ListEditor label="readiness changes" items={meeting.readiness_changes.map((r) => r.text)} placeholder="What moved on the readiness ladder…" onAdd={addReadiness} />
      </Section>
    </>
  )
}

function CreateBar({ today, onCreate, disabled }: { today: string; onCreate: DailyMeetingsWorkroomProps['onCreate']; disabled?: boolean }) {
  const [title, setTitle] = useState('')
  const [type, setType] = useState<MeetingType>('daily')
  const [date, setDate] = useState(today)
  const [creating, setCreating] = useState(false)
  const create = async () => {
    if (!title.trim() || disabled) return
    setCreating(true)
    try { await onCreate({ ...emptyMeetingDraft(date), meeting_title: title.trim(), meeting_type: type }); setTitle('') }
    finally { setCreating(false) }
  }
  return (
    <>
      <span className="sqv-ops-createbar__label">NEW MEETING</span>
      <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create() }} placeholder="Meeting title…" aria-label="New meeting title" disabled={disabled} className="sqv-ops-input sqv-ops-createbar__title" />
      <select value={type} onChange={(e) => setType(e.target.value as MeetingType)} aria-label="Meeting type" disabled={disabled} className="sqv-ops-input sqv-ops-createbar__fixed">
        {MEETING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Meeting date" disabled={disabled} className="sqv-ops-input sqv-ops-createbar__fixed" />
      <button type="button" onClick={create} disabled={disabled || creating} className="sqv-ops-btn">{creating ? 'Creating…' : 'Create'}</button>
    </>
  )
}

export default function DailyMeetingsWorkroom(props: DailyMeetingsWorkroomProps) {
  const { meetings, loading, error, authRequired, today, onCreate, onUpdate, onReload, onHome, onBackCommandCenter, onBackOperations } = props
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = useMemo(() => meetings.find((m) => m.id === selectedId) ?? meetings[0] ?? null, [meetings, selectedId])

  const rightRail = (
    <div className="sqv-surface sqv-ops sqv-ops-rail" style={accentVar}>
      <Section title="Past Meeting Receipts">
        {meetings.length === 0 ? (
          <p className="sqv-ops-empty">No past meetings yet — create today's meeting to start the record.</p>
        ) : (
          <div className="sqv-ops-dm-receipts">
            {meetings.map((m) => {
              const s = summarizeMeeting(m)
              const on = selected?.id === m.id
              return (
                <button key={m.id} type="button" onClick={() => setSelectedId(m.id)} className="sqv-ops-dm-receipt" data-selected={on}>
                  <div className="sqv-ops-dm-receipt__head">
                    <span className="sqv-ops-dm-receipt__title">{m.meeting_title}</span>
                    <span className="sqv-ops-dm-receipt__date">{m.meeting_date}</span>
                  </div>
                  <div className="sqv-ops-dm-receipt__sub">
                    {m.meeting_type} · {s.decisions} decisions · {s.openActions} open · {s.blockers} blockers{m.follow_up_needed ? ' · follow-up' : ''}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </Section>

      <Section title="Backend & integrations">
        <div className="sqv-ops-dm-backend">
          <StatusBadge status="WORKSPACE_READY" />
          <p>
            Meetings persist to <code>sq_daily_meetings</code> when you are signed into the live cockpit. Records are owner-scoped and every write is audited in OLIVIUM.
          </p>
          <div className="sqv-ops-dm-backend__integration">
            <StatusBadge status="INTEGRATION_DEFERRED" size="sm" />
            <span>Cal.com scheduling is a future integration only — not wired.</span>
          </div>
        </div>
      </Section>
    </div>
  )

  // Honest main-content states — never a fabricated meeting.
  let main: React.ReactNode
  if (authRequired) {
    main = (
      <Section title="Sign in required">
        <p className="sqv-ops-backend">
          Daily Meetings persist to the live cockpit. Sign in to create and load real meeting records — nothing is shown here until then (no demo meetings are fabricated).
        </p>
      </Section>
    )
  } else if (error) {
    main = (
      <Section title="Could not load meetings">
        <div className="sqv-ops-dm-stack">
          <p className="sqv-ops-queue-err">{error}</p>
          {onReload && <button type="button" onClick={onReload} className="sqv-ops-ghost">Retry</button>}
        </div>
      </Section>
    )
  } else if (loading && meetings.length === 0) {
    main = <Section title="Today's Meeting"><p className="sqv-ops-backend">Loading meetings…</p></Section>
  } else if (meetings.length === 0) {
    main = (
      <Section title="Today's Meeting">
        <p className="sqv-ops-backend">
          No meetings yet. Use <strong>New Meeting</strong> above to start today's record — decisions, blockers, action items, and readiness changes all persist.
        </p>
      </Section>
    )
  } else if (selected) {
    main = <MeetingEditor key={selected.id} meeting={selected} onUpdate={onUpdate} />
  }

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Operations', onClick: onBackOperations }}
      breadcrumb={['COMMAND CENTER', 'OPERATIONS', 'DAILY MEETINGS']}
      title="Daily Meetings"
      titleAccent={DAILY_MEETINGS_ACCENT}
      statusBadge={<StatusBadge status="WORKSPACE_READY" />}
      honestyBadge={{
        label: 'WORKING PORTAL · NOT FAKED',
        title: 'Daily Meetings persist to sq_daily_meetings when signed into the live cockpit. No demo meetings are fabricated — empty, loading, and error states are honest. Cal.com scheduling is INTEGRATION_DEFERRED (future only).',
      }}
      contextBar={<CreateBar today={today} onCreate={onCreate} disabled={authRequired} />}
      rightRail={rightRail}
    >
      <div className="sqv-surface sqv-ops sqv-ops-work" style={accentVar}>
        {main}
      </div>
    </CommandCenterShell>
  )
}
